import React, { useState, useRef, useEffect } from 'react';
import { TruncatedText } from './TruncatedText';
import { APP_CONFIG, formatDate } from '../../config/appConfig';
import { evaluateCondition } from '../../utils/expressionEvaluator';
import { ConditionalFormattingRule } from '../../services/conditionalFormattingService';
import { cn, mergeTailwindClasses } from '../../lib/utils';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';

export type FooterType = 'sum' | 'avg' | 'count' | 'custom';

/** Flatten a column header (string or React node) to plain text for exports / labels. */
export function getColumnHeaderText(header: React.ReactNode, fallback = ''): string {
  if (header == null || typeof header === 'boolean') return fallback;
  if (typeof header === 'string' || typeof header === 'number') return String(header);
  if (Array.isArray(header)) {
    const joined = header.map((h) => getColumnHeaderText(h)).filter(Boolean).join(' ');
    return joined || fallback;
  }
  if (React.isValidElement(header)) {
    const children = (header.props as { children?: React.ReactNode }).children;
    const fromChildren = getColumnHeaderText(children);
    return fromChildren || fallback;
  }
  return fallback;
}

export type ColumnGroupMeta = {
  /** Same `id` on adjacent columns merges them under one top header (`header`). */
  id: string;
  header: React.ReactNode;
};

export interface Column<T> {
  key: keyof T | string;
  header: React.ReactNode;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  /** When set on adjacent columns with the same `id`, DataTable renders a two-row header (group + leaf). */
  columnGroup?: ColumnGroupMeta;
  width?: string;
  /** Optional maximum width for this column (e.g. '200px') used to constrain very wide cells. */
  maxWidth?: string;
  align?: 'left' | 'center' | 'right';
  // Text truncation options
  maxLength?: number; // Maximum character length before truncation
  maxLines?: number; // Maximum number of lines before truncation (uses line-clamp)
  showTooltip?: boolean; // Show tooltip/popover with full content on focus (default: true)
  // Footer options
  footerType?: FooterType; // Type of footer calculation: 'sum', 'avg', 'count', or 'custom'
  footerRender?: (value: any, allData: T[]) => React.ReactNode; // Custom footer render function (used when footerType is 'custom')
  footerLabel?: string; // Custom label for footer (defaults to footerType)
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

type HeaderSegment<T> =
  | { kind: 'leaf'; column: Column<T> }
  | { kind: 'group'; groupId: string; groupHeader: React.ReactNode; columns: Column<T>[] };

function buildHeaderSegments<T>(columns: Column<T>[]): HeaderSegment<T>[] {
  const segments: HeaderSegment<T>[] = [];
  for (const column of columns) {
    const gid = column.columnGroup?.id;
    if (!gid) {
      segments.push({ kind: 'leaf', column });
      continue;
    }
    const gh = column.columnGroup!.header;
    const last = segments[segments.length - 1];
    if (last?.kind === 'group' && last.groupId === gid) {
      last.columns.push(column);
    } else {
      segments.push({ kind: 'group', groupId: gid, groupHeader: gh, columns: [column] });
    }
  }
  return segments;
}

function tableUsesColumnGroups<T>(columns: Column<T>[]): boolean {
  return columns.some((c) => c.columnGroup != null);
}

function columnDividerEndClass(
  columnDividers: boolean | undefined,
  _borderClass: string,
  cellIndex: number,
  rowCellCount: number,
): string {
  if (!columnDividers || rowCellCount <= 1) return '';
  if (cellIndex >= rowCellCount - 1) return '';
  return 'relative after:pointer-events-none after:absolute after:right-0 after:top-1.5 after:bottom-1.5 after:w-px after:bg-current after:opacity-25';
}

function headerDividerBottomClass(columnDividers: boolean | undefined): string {
  if (!columnDividers) return '';
  return 'relative before:pointer-events-none before:absolute before:left-0 before:right-0 before:bottom-0 before:h-px before:bg-current before:opacity-25';
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  pagination?: PaginationProps;
  sorting?: {
    sortConfig: SortConfig | null;
    onSort: (key: string) => void;
  };
  actions?: (row: T) => React.ReactNode;
  actionsPosition?: 'left' | 'right';
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  keyExtractor: (row: T) => string | number;
  density?: 'compact' | 'normal' | 'comfortable';
  conditionalFormattingRules?: ConditionalFormattingRule[];
  showFooter?: boolean; // Enable/disable footer row
  footerData?: T[]; // Optional data to use for footer calculations (defaults to all data if not provided)
  selectedRowId?: string | number | null; // ID of the currently selected row for highlighting
  /** Optional class overrides for a custom table look (e.g. portal) */
  containerClassName?: string;
  tableClassName?: string;
  theadClassName?: string;
  tbodyClassName?: string;
  thClassName?: string;
  trClassName?: string;
  tdClassName?: string;
  /** When set (e.g. 'w-full'), table wrapper fills container width so columns span full width */
  tableWrapperClassName?: string;
  /** When true, keep header row visible while scrolling table body */
  stickyHeader?: boolean;
  /** Vertical rules between columns in the header only (not body/footer). */
  columnDividers?: boolean;
  /** Optional refresh timestamp shown centered in pagination footer. */
  lastRefreshedAt?: Date | null;
}

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  pagination,
  sorting,
  actions,
  actionsPosition = 'right',
  emptyMessage = 'No data available',
  onRowClick,
  keyExtractor,
  density,
  conditionalFormattingRules = [],
  showFooter = false,
  footerData,
  selectedRowId,
  containerClassName,
  tableClassName,
  theadClassName,
  tbodyClassName,
  thClassName,
  trClassName,
  tdClassName,
  tableWrapperClassName,
  stickyHeader = true,
  columnDividers = false,
  lastRefreshedAt,
}: DataTableProps<T>) {
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const effectiveDensity = density || appConfig.table.defaultDensity;
  const densityConfig = APP_CONFIG.table.densityConfig[effectiveDensity] || APP_CONFIG.table.densityConfig[appConfig.table.defaultDensity];
  const [itemsPerPage, setItemsPerPage] = useState(pagination?.itemsPerPage || appConfig.table.defaultPageSize);
  const pageSizeOptions = appConfig.table.pageSizeOptions;
  const mainTableRef = useRef<HTMLTableElement>(null);
  const footerTableRef = useRef<HTMLTableElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const footerScrollRef = useRef<HTMLDivElement>(null);
  const syncingScrollRef = useRef(false);

  // Data to use for footer calculations (use footerData if provided, otherwise use all data)
  const footerCalculationData = footerData || data;
  const lastRefreshedLabel = lastRefreshedAt
    ? `Last refreshed at ${new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(lastRefreshedAt)}`
    : null;

  // Sync footer table column widths with main table
  useEffect(() => {
    if (showFooter && mainTableRef.current && footerTableRef.current) {
      const syncWidths = () => {
        const mainTable = mainTableRef.current;
        const footerTable = footerTableRef.current;
        
        if (!mainTable || !footerTable) return;
        
        // Align footer to body columns (works with single-row or grouped multi-row headers)
        const mainBodyCells = mainTable.querySelectorAll('tbody tr:first-child td');
        const footerCells = footerTable.querySelectorAll('tbody tr:first-child td');

        mainBodyCells.forEach((mainCell, index) => {
          if (footerCells[index]) {
            const mainCellElement = mainCell as HTMLElement;
            const footerCellElement = footerCells[index] as HTMLElement;
            
            // Get the actual rendered width
            const width = mainCellElement.offsetWidth;
            
            if (width > 0) {
              // Set width on both the cell and the col element
              footerCellElement.style.width = `${width}px`;
              footerCellElement.style.minWidth = `${width}px`;
              footerCellElement.style.maxWidth = `${width}px`;
              
              // Also update the colgroup col element
              const footerColgroup = footerTable.querySelector('colgroup');
              if (footerColgroup) {
                const footerCols = footerColgroup.querySelectorAll('col');
                if (footerCols[index]) {
                  (footerCols[index] as HTMLElement).style.width = `${width}px`;
                  (footerCols[index] as HTMLElement).style.minWidth = `${width}px`;
                  (footerCols[index] as HTMLElement).style.maxWidth = `${width}px`;
                }
              }
            }
          }
        });
      };
      
      // Sync immediately
      syncWidths();
      
      // Also sync after a short delay to ensure main table is fully rendered
      const timeoutId1 = setTimeout(syncWidths, 10);
      const timeoutId2 = setTimeout(syncWidths, 100);
      
      // Sync on window resize
      window.addEventListener('resize', syncWidths);
      
      // Use ResizeObserver to sync when main table resizes
      let resizeObserver: ResizeObserver | null = null;
      if (mainTableRef.current) {
        resizeObserver = new ResizeObserver(syncWidths);
        resizeObserver.observe(mainTableRef.current);
      }
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        window.removeEventListener('resize', syncWidths);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [showFooter, data, columns]);

  // Keep footer totals aligned with body when either scrolls horizontally
  useEffect(() => {
    if (!showFooter) return;
    const body = bodyScrollRef.current;
    const footer = footerScrollRef.current;
    if (!body || !footer) return;

    const align = (source: HTMLElement, target: HTMLElement) => {
      if (target.scrollLeft === source.scrollLeft) return;
      syncingScrollRef.current = true;
      target.scrollLeft = source.scrollLeft;
      requestAnimationFrame(() => {
        syncingScrollRef.current = false;
      });
    };

    const syncFrom = (source: HTMLElement, target: HTMLElement) => () => {
      if (syncingScrollRef.current) return;
      align(source, target);
    };

    const onBodyScroll = syncFrom(body, footer);
    const onFooterScroll = syncFrom(footer, body);
    body.addEventListener('scroll', onBodyScroll, { passive: true });
    footer.addEventListener('scroll', onFooterScroll, { passive: true });

    // Align after layout / column-width sync settles
    footer.scrollLeft = body.scrollLeft;
    const t1 = window.setTimeout(() => align(body, footer), 20);
    const t2 = window.setTimeout(() => align(body, footer), 120);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      body.removeEventListener('scroll', onBodyScroll);
      footer.removeEventListener('scroll', onFooterScroll);
    };
  }, [showFooter, data, columns]);

  const handleSort = (key: string) => {
    if (sorting && columns.find((col) => col.key === key)?.sortable) {
      sorting.onSort(key);
    }
  };

  const getSortIcon = (key: string) => {
    if (!sorting || !sorting.sortConfig || sorting.sortConfig.key !== key) {
      return (
        <svg className={`w-4 h-4 ${colors.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    if (sorting.sortConfig.direction === 'asc') {
      return (
        <svg className={cn('w-4 h-4', colors.primaryFg)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }

    return (
      <svg className={cn('w-4 h-4', colors.primaryFg)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Helper: Get matching column-level formatting rules
  const getColumnFormattingRules = (columnKey: string, cellValue: any, row: T): ConditionalFormattingRule[] => {
    return conditionalFormattingRules
      .filter(rule => rule.isActive && rule.scope === 'column')
      .filter(rule => {
        // For column scope, check if it applies to this column
        if (rule.condition.type === 'column') {
          return rule.condition.columnKey === columnKey || rule.columnKey === columnKey;
        } else {
          // Expression-based rules apply to the reference column
          return rule.columnKey === columnKey;
        }
      })
      .filter(rule => {
        return evaluateCondition(rule.condition, row as Record<string, any>, columnKey);
      })
      .sort((a, b) => b.priority - a.priority);
  };

  // Helper: Get matching row-level formatting rules
  const getRowFormattingRules = (row: T): ConditionalFormattingRule[] => {
    return conditionalFormattingRules
      .filter(rule => rule.isActive && rule.scope === 'row')
      .filter(rule => {
        return evaluateCondition(rule.condition, row as Record<string, any>, rule.columnKey);
      })
      .sort((a, b) => b.priority - a.priority);
  };

  // Helper: Combine styles from multiple rules
  const combineStyles = (rules: ConditionalFormattingRule[]): string => {
    if (rules.length === 0) return '';
    
    const styleParts: string[] = [];
    rules.forEach(rule => {
      if (rule.style.backgroundColor) styleParts.push(rule.style.backgroundColor);
      if (rule.style.textColor) styleParts.push(rule.style.textColor);
      if (rule.style.fontWeight) styleParts.push(rule.style.fontWeight);
      if (rule.style.borderColor) styleParts.push(rule.style.borderColor);
    });
    
    return styleParts.join(' ');
  };

  // Calculate footer value for a column
  const calculateFooterValue = (column: Column<T>): React.ReactNode => {
    if (!column.footerType) return null;

    switch (column.footerType) {
      case 'sum': {
        const values = footerCalculationData
          .map(row => {
            const value = column.key in row ? row[column.key as keyof T] : null;
            // Try to parse as number
            if (value === null || value === undefined) return 0;
            const numValue = typeof value === 'number' ? value : parseFloat(String(value));
            return isNaN(numValue) ? 0 : numValue;
          })
          .filter(v => !isNaN(v));
        
        const sum = values.reduce((acc, val) => acc + val, 0);
        
        // Use custom render if provided, otherwise format as number
        if (column.footerRender) {
          return column.footerRender(sum, footerCalculationData);
        }
        
        // Format number with locale
        return typeof sum === 'number' && !isNaN(sum)
          ? sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '-';
      }
      
      case 'avg': {
        const values = footerCalculationData
          .map(row => {
            const value = column.key in row ? row[column.key as keyof T] : null;
            if (value === null || value === undefined) return null;
            const numValue = typeof value === 'number' ? value : parseFloat(String(value));
            return isNaN(numValue) ? null : numValue;
          })
          .filter((v): v is number => v !== null && !isNaN(v));
        
        if (values.length === 0) return '-';
        
        const avg = values.reduce((acc, val) => acc + val, 0) / values.length;
        
        // Use custom render if provided, otherwise format as number
        if (column.footerRender) {
          return column.footerRender(avg, footerCalculationData);
        }
        
        return avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      
      case 'count': {
        const count = footerCalculationData.length;
        
        // Use custom render if provided, otherwise show count
        if (column.footerRender) {
          return column.footerRender(count, footerCalculationData);
        }
        
        return count.toLocaleString('en-US');
      }
      
      case 'custom': {
        if (column.footerRender) {
          return column.footerRender(null, footerCalculationData);
        }
        return '-';
      }
      
      default:
        return null;
    }
  };

  // Get footer label for a column
  const getFooterLabel = (column: Column<T>): string => {
    if (column.footerLabel) return column.footerLabel;
    
    switch (column.footerType) {
      case 'sum':
        return 'Sum';
      case 'avg':
        return 'Avg';
      case 'count':
        return 'Count';
      case 'custom':
        return '';
      default:
        return '';
    }
  };

  const renderCell = (column: Column<T>, row: T) => {
    const value = column.key in row ? row[column.key as keyof T] : null;

    // Get column-level formatting rules
    const columnRules = getColumnFormattingRules(String(column.key), value, row);
    const columnStyles = combineStyles(columnRules);

    let cellContent;
    if (column.render) {
      cellContent = column.render(value, row);
    } else {
      // Default rendering
      // Handle objects - convert to string or extract meaningful value
      let displayValue: any = value;
      if (typeof value === 'object' && value !== null && !React.isValidElement(value)) {
        // If it's an object with common description fields, extract them
        if ('pst_descr' in value) {
          displayValue = (value as any).pst_descr || JSON.stringify(value);
        } else if ('pit_descr' in value) {
          displayValue = (value as any).pit_descr || JSON.stringify(value);
        } else if ('pic_descr' in value) {
          displayValue = (value as any).pic_descr || JSON.stringify(value);
        } else if ('pts_descr' in value) {
          displayValue = (value as any).pts_descr || JSON.stringify(value);
        } else if ('ts_descr' in value) {
          displayValue = (value as any).ts_descr || JSON.stringify(value);
        } else {
          displayValue = JSON.stringify(value);
        }
      }
      if (displayValue === null || displayValue === undefined) {
        return <span className={colors.textMuted}>-</span>;
      }

      if (typeof displayValue === 'boolean') {
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              displayValue ? colors.success : colors.error
            }`}
          >
            {displayValue ? 'Yes' : 'No'}
          </span>
        );
      }

      // Check if displayValue is a Date object or date string
      if (typeof displayValue === 'object' && displayValue !== null && displayValue.constructor === Date) {
        return formatDate(displayValue);
      }
      
      if (typeof displayValue === 'string' && displayValue.match(/^\d{4}-\d{2}-\d{2}/)) {
        return formatDate(displayValue);
      }

      // If column has truncation options and displayValue is a string, use TruncatedText
      if (typeof displayValue === 'string' && (column.maxLength || column.maxLines)) {
        return (
          <TruncatedText
            text={displayValue}
            maxLength={column.maxLength}
            maxLines={column.maxLines}
            className={`text-sm ${colors.text}`}
            showTooltip={column.showTooltip !== false}
          />
        );
      }

      cellContent = String(displayValue);
    }

    // Apply column-level styles to both custom render and default content
    if (columnStyles) {
      // If cellContent is already a React element, try to clone it and add classes
      if (React.isValidElement(cellContent)) {
        // Clone the element and merge className props using smart Tailwind merging
        const existingClassName = (cellContent.props as any)?.className || '';
        const mergedClassName = mergeTailwindClasses(existingClassName, columnStyles);
        
        return React.cloneElement(cellContent as React.ReactElement<any>, {
          className: mergedClassName,
        } as any);
      }
      // Otherwise, wrap in a span with styles
      return (
        <span className={columnStyles}>
          {cellContent}
        </span>
      );
    }

    // If cellContent is a plain string, wrap it to ensure it uses the correct text color
    if (typeof cellContent === 'string') {
      return <span className={colors.text}>{cellContent}</span>;
    }

    return cellContent;
  };

  if (!loading && data.length === 0) {
    return (
      <div className={`${colors.surface} rounded-lg shadow-sm border ${colors.border} p-8`}>
        <div className="text-center">
          <svg
            className={`mx-auto h-12 w-12 ${colors.textMuted}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className={`mt-2 text-sm font-medium ${colors.text}`}>No data</h3>
          <p className={`mt-1 text-sm ${colors.textMuted}`}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const useColumnGroups = tableUsesColumnGroups(columns);
  const headerSegments = buildHeaderSegments(columns);
  const leftAct = Boolean(actions && actionsPosition === 'left');
  const rightAct = Boolean(actions && actionsPosition === 'right');
  const bodyRowCellCount = (leftAct ? 1 : 0) + columns.length + (rightAct ? 1 : 0);
  const groupedHeaderRow1CellCount = (leftAct ? 1 : 0) + headerSegments.length + (rightAct ? 1 : 0);
  const groupedHeaderRow2Cells = headerSegments.flatMap((seg) => (seg.kind === 'group' ? seg.columns : []));
  const groupedHeaderRow2CellCount = groupedHeaderRow2Cells.length;
  const showSkeletonRows = loading && data.length === 0;
  const showRefreshingStrip = loading && data.length > 0;
  const skeletonRowCount = pagination?.itemsPerPage ? Math.min(Math.max(pagination.itemsPerPage, 5), 10) : 8;

  // Generate colgroup for consistent column widths
  const colgroup = (
    <colgroup>
      {actions && actionsPosition === 'left' && <col style={{ width: 'auto' }} />}
      {columns.map((column) => (
        <col
          key={String(column.key)}
          style={{
            width: column.width || 'auto',
            // Allow presets to cap how wide a column can grow
            maxWidth: column.maxWidth,
          }}
        />
      ))}
      {actions && actionsPosition === 'right' && <col style={{ width: 'auto' }} />}
    </colgroup>
  );

  return (
    <div className={cn('h-full flex flex-col overflow-hidden', colors.surface, 'rounded-lg shadow-sm border', colors.border, containerClassName)}>
      {showRefreshingStrip && (
        <div className={cn('flex items-center justify-center gap-2 border-b px-3 py-1.5 text-xs', colors.border, colors.surfaceHover, colors.textSecondary)}>
          <span className={cn('inline-block h-2 w-2 animate-pulse rounded-full', colors.primary)} aria-hidden />
          <span>Refreshing data...</span>
        </div>
      )}
      <div ref={bodyScrollRef} className="flex-1 min-h-0 overflow-auto dashboard-scrollbar">
        <div className={cn('align-middle', tableWrapperClassName ?? 'inline-block min-w-max')}>
          <table ref={mainTableRef} className={cn('min-w-full', !tableClassName && `divide-y ${colors.border}`, tableClassName)} style={{ tableLayout: 'auto' }}>
            {colgroup}
          <thead
            className={cn(
              colors.surfaceHover,
              stickyHeader && 'sticky top-0 z-10',
              theadClassName
            )}
          >
            {!useColumnGroups ? (
              <tr>
                {actions && actionsPosition === 'left' && (
                  <th
                    scope="col"
                    className={cn(
                      densityConfig.headerPadding,
                      'text-left text-xs font-medium',
                      headerDividerBottomClass(columnDividers),
                      colors.textSecondary,
                      'uppercase tracking-wider',
                      columnDividerEndClass(columnDividers, colors.border, 0, bodyRowCellCount),
                      thClassName,
                    )}
                  >
                    Actions
                  </th>
                )}
                {columns.map((column, colIdx) => (
                  <th
                    key={String(column.key)}
                    scope="col"
                    className={cn(
                      densityConfig.headerPadding,
                      'text-left text-xs font-medium',
                      headerDividerBottomClass(columnDividers),
                      colors.textSecondary,
                      'uppercase tracking-wider',
                      column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                      columnDividerEndClass(columnDividers, colors.border, (leftAct ? 1 : 0) + colIdx, bodyRowCellCount),
                      thClassName,
                    )}
                  >
                    <div
                      className={`flex items-center space-x-1 ${
                        column.sortable && sorting
                          ? `cursor-pointer hover:${colors.textSecondary} select-none`
                          : ''
                      } ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}
                      onClick={() => column.sortable && handleSort(String(column.key))}
                    >
                      <span>{column.header}</span>
                      {column.sortable && sorting && getSortIcon(String(column.key))}
                    </div>
                  </th>
                ))}
                {actions && actionsPosition === 'right' && (
                  <th
                    scope="col"
                    className={cn(
                      densityConfig.headerPadding,
                      'text-right text-xs font-medium',
                      headerDividerBottomClass(columnDividers),
                      colors.textSecondary,
                      'uppercase tracking-wider',
                      columnDividerEndClass(columnDividers, colors.border, bodyRowCellCount - 1, bodyRowCellCount),
                      thClassName,
                    )}
                  >
                    Actions
                  </th>
                )}
              </tr>
            ) : (
              <>
                <tr>
                  {actions && actionsPosition === 'left' && (
                    <th
                      rowSpan={2}
                      scope="col"
                      className={cn(
                        densityConfig.headerPadding,
                        'text-left align-middle text-xs font-medium',
                        headerDividerBottomClass(columnDividers),
                        colors.textSecondary,
                        'uppercase tracking-wider',
                        columnDividerEndClass(columnDividers, colors.border, 0, groupedHeaderRow1CellCount),
                        thClassName,
                      )}
                    >
                      Actions
                    </th>
                  )}
                  {headerSegments.map((seg, segIdx) =>
                    seg.kind === 'leaf' ? (
                      <th
                        key={String(seg.column.key)}
                        rowSpan={2}
                        scope="col"
                        className={cn(
                          densityConfig.headerPadding,
                          'align-middle text-left text-xs font-medium',
                          headerDividerBottomClass(columnDividers),
                          colors.textSecondary,
                          'uppercase tracking-wider',
                          seg.column.align === 'right' ? 'text-right' : seg.column.align === 'center' ? 'text-center' : 'text-left',
                          columnDividerEndClass(
                            columnDividers,
                            colors.border,
                            (leftAct ? 1 : 0) + segIdx,
                            groupedHeaderRow1CellCount,
                          ),
                          thClassName,
                        )}
                      >
                        <div
                          className={`flex items-center space-x-1 ${
                            seg.column.sortable && sorting
                              ? `cursor-pointer hover:${colors.textSecondary} select-none`
                              : ''
                          } ${seg.column.align === 'right' ? 'justify-end' : seg.column.align === 'center' ? 'justify-center' : ''}`}
                          onClick={() => seg.column.sortable && handleSort(String(seg.column.key))}
                        >
                          <span>{seg.column.header}</span>
                          {seg.column.sortable && sorting && getSortIcon(String(seg.column.key))}
                        </div>
                      </th>
                    ) : (
                      <th
                        key={seg.groupId}
                        colSpan={seg.columns.length}
                        scope="colgroup"
                        className={cn(
                          densityConfig.headerPadding,
                          headerDividerBottomClass(columnDividers),
                          colors.border,
                          'text-center text-xs font-semibold normal-case tracking-normal',
                          colors.text,
                          columnDividerEndClass(
                            columnDividers,
                            colors.border,
                            (leftAct ? 1 : 0) + segIdx,
                            groupedHeaderRow1CellCount,
                          ),
                          thClassName,
                        )}
                      >
                        {seg.groupHeader}
                      </th>
                    )
                  )}
                  {actions && actionsPosition === 'right' && (
                    <th
                      rowSpan={2}
                      scope="col"
                      className={cn(
                        densityConfig.headerPadding,
                        'text-right align-middle text-xs font-medium',
                        headerDividerBottomClass(columnDividers),
                        colors.textSecondary,
                        'uppercase tracking-wider',
                        columnDividerEndClass(
                          columnDividers,
                          colors.border,
                          groupedHeaderRow1CellCount - 1,
                          groupedHeaderRow1CellCount,
                        ),
                        thClassName,
                      )}
                    >
                      Actions
                    </th>
                  )}
                </tr>
                <tr>
                  {groupedHeaderRow2Cells.map((column, subIdx) => (
                    <th
                      key={String(column.key)}
                      scope="col"
                      className={cn(
                        densityConfig.headerPadding,
                        'text-left text-[0.65rem] font-medium',
                        headerDividerBottomClass(columnDividers),
                        colors.textSecondary,
                        'uppercase tracking-wider',
                        column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                        columnDividerEndClass(columnDividers, colors.border, subIdx, groupedHeaderRow2CellCount),
                        thClassName,
                      )}
                    >
                      <div
                        className={`flex items-center space-x-1 ${
                          column.sortable && sorting
                            ? `cursor-pointer hover:${colors.textSecondary} select-none`
                            : ''
                        } ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}
                        onClick={() => column.sortable && handleSort(String(column.key))}
                      >
                        <span>{column.header}</span>
                        {column.sortable && sorting && getSortIcon(String(column.key))}
                      </div>
                    </th>
                  ))}
                </tr>
              </>
            )}
          </thead>
          <tbody className={cn(colors.surface, !tbodyClassName && `divide-y ${colors.border}`, tbodyClassName)}>
            {showSkeletonRows
              ? Array.from({ length: skeletonRowCount }, (_, i) => (
                  <tr key={`skeleton-${i}`} className={cn('animate-pulse', trClassName)}>
                    {actions && actionsPosition === 'left' && (
                      <td
                        className={cn(
                          densityConfig.cellPadding,
                          'whitespace-nowrap text-left text-sm font-medium',
                          colors.text,
                          tdClassName,
                        )}
                      >
                        <div className="flex items-center justify-start">
                          <span className={cn('h-6 w-14 rounded-md', colors.surfaceHover)} />
                        </div>
                      </td>
                    )}
                    {columns.map((column, colIdx) => {
                      const widths = ['w-3/5', 'w-2/5', 'w-4/5', 'w-1/2'];
                      const widthClass = widths[colIdx % widths.length];
                      return (
                        <td
                          key={`skeleton-cell-${String(column.key)}-${i}`}
                          className={cn(
                            densityConfig.cellPadding,
                            'text-sm',
                            colors.text,
                            column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                            typeof column.width === 'string' ? 'whitespace-nowrap' : 'whitespace-normal sm:whitespace-nowrap',
                            column.maxWidth && 'overflow-hidden text-ellipsis',
                            tdClassName,
                          )}
                          style={column.maxWidth ? { maxWidth: column.maxWidth } : undefined}
                        >
                          <div
                            className={cn(
                              'inline-block h-4 rounded-md align-middle',
                              colors.surfaceHover,
                              widthClass,
                              column.align === 'right' && 'ml-auto',
                              column.align === 'center' && 'mx-auto',
                            )}
                          />
                        </td>
                      );
                    })}
                    {actions && actionsPosition === 'right' && (
                      <td
                        className={cn(
                          densityConfig.cellPadding,
                          'whitespace-nowrap text-right text-sm font-medium',
                          colors.text,
                          tdClassName,
                        )}
                      >
                        <div className="flex items-center justify-end">
                          <span className={cn('h-6 w-14 rounded-md', colors.surfaceHover)} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              : data.map((row) => {
              // Get row-level formatting rules
              const rowRules = getRowFormattingRules(row);
              const rowStyles = combineStyles(rowRules);
              const rowId = keyExtractor(row);
              const isSelected = selectedRowId !== undefined && selectedRowId !== null && rowId === selectedRowId;
              
              return (
                <tr
                  key={rowId}
                  data-row-id={rowId}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={cn(
                    onRowClick && 'cursor-pointer',
                    colors.interactiveHover,
                    isSelected && cn(colors.info, 'border-l-4', colors.primaryBorder),
                    rowStyles,
                    trClassName,
                  )}
                >
                {actions && actionsPosition === 'left' && (
                  <td
                    className={cn(
                      densityConfig.cellPadding,
                      'whitespace-nowrap text-left text-sm font-medium',
                      colors.text,
                      tdClassName,
                    )}
                  >
                    <div className="flex items-center flex-nowrap gap-1 justify-start">
                      {actions(row)}
                    </div>
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={String(column.key)}
                    className={cn(
                      densityConfig.cellPadding,
                      'text-sm',
                      colors.text,
                      column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                      typeof column.width === 'string' ? 'whitespace-nowrap' : 'whitespace-normal sm:whitespace-nowrap',
                      column.maxWidth && 'overflow-hidden text-ellipsis',
                      tdClassName,
                    )}
                    style={column.maxWidth ? { maxWidth: column.maxWidth } : undefined}
                  >
                    {renderCell(column, row)}
                  </td>
                ))}
                {actions && actionsPosition === 'right' && (
                  <td
                    className={cn(
                      densityConfig.cellPadding,
                      'whitespace-nowrap text-right text-sm font-medium',
                      colors.text,
                      tdClassName,
                    )}
                  >
                    <div className="flex items-center flex-nowrap gap-1 justify-end">
                      {actions(row)}
                    </div>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Footer - Outside vertical scroll; horizontal scroll synced with body */}
      {showFooter && (
        <div
          ref={footerScrollRef}
          className={cn(colors.surfaceHover, 'border-t-2 overflow-x-auto dashboard-scrollbar', colors.border)}
        >
          <div className="inline-block min-w-max align-middle">
            <table
              ref={footerTableRef}
              className={cn('min-w-max divide-y', colors.border)}
              style={{ tableLayout: 'fixed' }}
            >
              {colgroup}
              <tbody>
                <tr>
                  {actions && actionsPosition === 'left' && (
                    <td
                      className={cn(
                        densityConfig.cellPadding,
                        'text-sm font-semibold text-left',
                        colors.textSecondary,
                      )}
                    >
                      {/* Actions column footer is intentionally left empty */}
                    </td>
                  )}
                  {columns.map((column) => {
                    const footerValue = calculateFooterValue(column);
                    const footerLabel = getFooterLabel(column);
                    
                    return (
                      <td
                        key={String(column.key)}
                        className={cn(
                          densityConfig.cellPadding,
                          'text-sm font-semibold',
                          colors.text,
                          column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                        )}
                      >
                        {footerValue !== null && footerValue !== '' && String(footerValue).trim() !== '' ? (
                          <div className="flex flex-col">
                            {footerLabel && (
                              <span className={`text-xs ${colors.textMuted} uppercase tracking-wider mb-0.5`}>
                                {footerLabel}
                              </span>
                            )}
                            <span>{footerValue}</span>
                          </div>
                        ) : column.footerType === 'custom' && footerValue === null ? (
                          // Empty cell for custom footer that returns null (intentionally empty)
                          <span>&nbsp;</span>
                        ) : (
                          <span className={colors.textMuted}>-</span>
                        )}
                      </td>
                    );
                  })}
                  {actions && actionsPosition === 'right' && (
                    <td
                      className={cn(
                        densityConfig.cellPadding,
                        'text-sm font-semibold text-right',
                        colors.textSecondary,
                      )}
                    >
                      {/* Actions column footer is intentionally left empty */}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && (
        <div className={cn(colors.surface, 'relative border-t px-4 py-3 sm:px-6', colors.border)}>
          {lastRefreshedLabel && (
            <div className="pointer-events-none absolute inset-x-0 hidden justify-center sm:flex">
              <p
                className={cn(
                  'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium tabular-nums',
                  colors.border,
                  colors.textMuted,
                )}
              >
                {lastRefreshedLabel}
              </p>
            </div>
          )}
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={cn(
                'relative inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                colors.border,
                colors.textSecondary,
                colors.surface,
                colors.interactiveHover,
              )}
            >
              Previous
            </button>
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className={cn(
                'relative ml-3 inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                colors.border,
                colors.textSecondary,
                colors.surface,
                colors.interactiveHover,
              )}
            >
              Next
            </button>
          </div>

          <div className="hidden sm:flex sm:items-center sm:justify-between">
            <div>
              <p className={`text-sm ${colors.textSecondary}`}>
                Showing{' '}
                <span className="font-medium">
                  {pagination.currentPage === 1
                    ? 1
                    : (pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                </span>{' '}
                of <span className="font-medium">{pagination.totalItems}</span> results
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {pagination.onItemsPerPageChange && (
                <div className="flex items-center space-x-2">
                  <label className={`text-sm ${colors.textSecondary}`}>Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = parseInt(e.target.value);
                      setItemsPerPage(newItemsPerPage);
                      pagination.onItemsPerPageChange?.(newItemsPerPage);
                    }}
                    className={cn(
                      'border rounded-md px-3 py-1 text-sm focus:ring-2',
                      colors.border,
                      colors.text,
                      colors.surface,
                      colors.primaryBorderAll,
                      colors.primaryBorderAll.replace('border-', 'focus:ring-'),
                    )}
                  >
                    {pageSizeOptions.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={cn(
                    'relative inline-flex items-center rounded-l-md border px-2 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                    colors.border,
                    colors.surface,
                    colors.textMuted,
                    colors.interactiveHover,
                  )}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first page, last page, current page, and pages around current
                    return (
                      page === 1 ||
                      page === pagination.totalPages ||
                      (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                    );
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <span className={`relative inline-flex items-center px-4 py-2 border ${colors.border} ${colors.surface} text-sm font-medium ${colors.textSecondary}`}>
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => pagination.onPageChange(page)}
                          className={cn(
                            'relative inline-flex items-center border px-4 py-2 text-sm font-medium',
                            page === pagination.currentPage
                              ? cn('z-10', colors.info, colors.primaryBorderAll, colors.primaryFg)
                              : cn(colors.surface, colors.border, colors.textMuted, colors.interactiveHover),
                          )}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
                <button
                  onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={cn(
                    'relative inline-flex items-center rounded-r-md border px-2 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                    colors.border,
                    colors.surface,
                    colors.textMuted,
                    colors.interactiveHover,
                  )}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;

